import { useNotification } from '@topcon/topcon-utils';
import { useTranslation } from 'react-i18next';
import { Statuses } from '@topcon/tuicl/dist/common/component-types';
import { useCallback } from 'react';

import {
  useSubscriptions,
  useTaxonomy,
} from '../pages/Settings/Notifications/notificationPreferences.queries';
import { SubscriptionsInput, Aspect } from '../api/generated/notification-pref';
import { useUpdateNotificationPreference } from '../pages/Settings/Notifications/notificationPreferences.mutations';
import { useErrorDrawer } from '../providers/ErrorDrawerContext/error-drawer.context';

const useNotificationSettings = (aspect: Aspect | 'user' | 'identity' = Aspect.USER) => {
  const {
    data: subscriptionsData,
    isLoading: isLoadingSubscriptions,
    isError: isSubscriptionsError,
  } = useSubscriptions(aspect);
  const {
    isLoading: isLoadingTaxonomy,
    isError: isTaxonomyError,
    data: taxonomyData,
  } = useTaxonomy();
  const { mutateAsync: updateNotificationPreference, isPending: isUpdatingPreference } =
    useUpdateNotificationPreference();
  const { t } = useTranslation();
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();

  const handleUpdateChannel = useCallback(
    async (payload: { subscriptions: SubscriptionsInput }) => {
      try {
        await updateNotificationPreference({ ...payload, aspect });
      } catch (e) {
        if (showError(e)) {
          return;
        }
        notificationService.addNotification(
          t('notifications.messages.updatePreferenceError'),
          '',
          Statuses.error
        );
        return Promise.reject(e);
      }
    },
    [updateNotificationPreference, notificationService, t, aspect, showError]
  );

  return {
    subscriptionsData,
    isLoadingSubscriptions,
    taxonomyData,
    isLoadingTaxonomy,
    isError: isSubscriptionsError || isTaxonomyError,
    isUpdatingPreference,
    handleUpdateChannel,
  };
};

export default useNotificationSettings;
